import { useCallback, useEffect, useRef, useState } from 'react';
import { axiosInstance } from '../config';
import { medicineDraftModels } from './medicineDraftModels.mjs';
import { requestErrorDetails } from './requestError.mjs';

const modes = new Set(['auto', 'free', 'paid']);
export default function useResearchModels() {
  const [preferred, setPreferred] = useState(() => {
    try { return localStorage.getItem('research.preferredModel') || ''; }
    catch { return ''; }
  });
  const [billingMode, setMode] = useState(() => {
    try { const value = localStorage.getItem('research.billingMode'); return modes.has(value) ? value : 'auto'; }
    catch { return 'auto'; }
  });
  const [state, setState] = useState({ mode: '', catalogue: null, error: '', loading: true });
  const request = useRef(0);
  const load = useCallback(async (mode, refresh = false) => {
    const sequence = ++request.current;
    setState({ mode, catalogue: null, error: '', loading: true });
    try {
      const response = refresh
        ? await axiosInstance.post('/api/translations/research/models/refresh', {}, { params: { mode }, timeout: 15000 })
        : await axiosInstance.get('/api/translations/research/models', { params: { mode }, timeout: 15000 });
      if (request.current === sequence) setState({ mode, catalogue: response.data, error: '', loading: false });
    } catch (failure) {
      if (request.current === sequence) setState({ mode, catalogue: null, loading: false,
        error: requestErrorDetails(failure, { readOnly: true, fallback: 'Generation models could not be loaded. Try refreshing models.' }).message });
    }
  }, []);
  const cancelLoad = useCallback(() => { request.current++; }, []);
  useEffect(() => {
    load(billingMode);
    return cancelLoad;
  }, [billingMode, load, cancelLoad]);
  const current = state.mode === billingMode ? state.catalogue : null;
  const effective = medicineDraftModels(current, preferred);
  useEffect(() => {
    if (!effective.selection || effective.selection === preferred) return;
    setPreferred(effective.selection);
    try { localStorage.setItem('research.preferredModel', effective.selection); } catch {}
  }, [effective.selection, preferred]);

  return {
    ...effective,
    billingMode,
    error: state.mode === billingMode ? state.error : '',
    loading: state.mode !== billingMode || state.loading,
    setBillingMode: value => {
      if (!modes.has(value)) return;
      setMode(value);
      try { localStorage.setItem('research.billingMode', value); } catch {}
    },
    setSelection: value => {
      if (!effective.catalogue?.models.some(model => model.id === value && model.enabled)) return;
      setPreferred(value);
      try { localStorage.setItem('research.preferredModel', value); } catch {}
    },
    refresh: () => load(billingMode, true),
  };
}
